import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BellRing, CalendarHeart, FileLock2, FileSpreadsheet, IdCard, RefreshCw, Timer, Users, Check } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CHIPS = [
  [IdCard, "Document expiries"], [CalendarHeart, "Birthdays & anniversaries"], [BellRing, "Smart reminders"],
  [FileLock2, "Files attached to reminders"], [FileSpreadsheet, "Excel import & export"],
];
const FEATURES = [
  { icon: CalendarHeart, title: "Track Every Date", text: "One organized place for dates that matter — from passports and visas to birthdays, subscriptions and leases." },
  { icon: Timer, title: "See Your Countdown", text: "Know what is approaching and what is overdue. Countdowns follow your own time zone, every day." },
  { icon: FileLock2, title: "Stay Organized", text: "Keep documents and reminder details together, privately attached to the reminder they belong to." },
  { icon: RefreshCw, title: "Renew with History", text: "Start a renewal, tick off a checklist, record the new expiry and keep the previous date and files on record." },
  { icon: Users, title: "People & Assets", text: "Group reminders by family members, staff, vehicles, properties or organizations." },
  { icon: FileSpreadsheet, title: "Excel In, Excel Out", text: "Import from our template with row-by-row validation, then export filtered reminders whenever you need." },
];
const STEPS = [
  ["Pin It.", "Add a date in seconds with Quick Add, or use a guided form for passports, insurance, birthdays and more."],
  ["Track It.", "Live countdowns, a calendar and a dashboard show what's due soon and what's overdue."],
  ["Never Miss It.", "Alerts at the offsets you choose — 90, 30, 7 days before, or your own — in your Notification Center and by email if you opt in."],
];
const FAQ = [
  ["Is PinMyDates free?", "PinMyDates is in its early release. Pricing has not been announced; any future paid plans will be published clearly before anyone is charged."],
  ["How do reminders reach me?", "Every alert appears in your in-app Notification Center. You can also opt in to email reminders in Settings, and send yourself a test email first."],
  ["Can PinMyDates read my documents automatically?", "Not yet. You can upload a document and enter its details yourself. Automatic reading of documents is planned for a later release and is not active today."],
  ["Who can see my files?", "Only you. Files are stored privately and every download is checked against your signed-in account."],
  ["Can I delete my account?", "Yes — from Settings → Privacy & Data → Delete Account, or by emailing pinmydates@gmail.com. See the Delete Account page for details."],
];

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/dashboard" : "/login";
  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-8 lg:pt-12">
        <div className="relative overflow-hidden rounded-[28px] border border-[var(--pm-border)] pm-hero-bg px-6 sm:px-12 lg:px-16 py-14 lg:py-20 grid lg:grid-cols-[1.25fr_1fr] gap-12 items-center" data-testid="landing-hero">
          <div className="pm-fade-up">
            <p className="text-xs sm:text-sm font-extrabold tracking-[0.16em] uppercase text-[#A0136E] mb-5" data-testid="hero-tagline">{BRAND.tagline}</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-[-0.04em]" data-testid="hero-headline">
              Never Miss<br /><span className="pm-gradient-text">What Matters.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[var(--pm-muted)] max-w-xl leading-relaxed" data-testid="hero-subheading">{BRAND.subheading}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={cta} className="pm-btn pm-btn-primary h-12 px-6" data-testid="hero-get-started-btn">Get Started Free <ArrowRight className="w-4 h-4" /></Link>
              <a href="#features" className="pm-btn pm-btn-outline h-12 px-6" data-testid="hero-explore-btn">Explore Features</a>
            </div>
            <p className="mt-8 font-extrabold text-lg pm-gradient-text inline-block" data-testid="hero-slogan">{BRAND.slogan}</p>
          </div>
          <div className="flex justify-center lg:justify-end pm-fade-up" style={{ animationDelay: ".15s" }}>
            <div className="relative">
              <div className="absolute -inset-10 rounded-full blur-3xl opacity-40" style={{ background: "var(--pm-gradient)" }} />
              <div className="relative bg-white rounded-[36px] p-3 shadow-[0_30px_60px_-20px_rgba(56,16,125,.45)] rotate-[-3deg] hover:rotate-0 transition-transform duration-500">
                <img src={BRAND.logo} alt="PinMyDates — Every Date Matters." className="w-64 sm:w-80 rounded-[28px]" data-testid="hero-logo" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-6" data-testid="feature-chips">
          {CHIPS.map(([Icon, t]) => (
            <span key={t} className="pm-card !rounded-xl px-4 py-2.5 text-sm font-semibold flex items-center gap-2"><Icon className="w-4 h-4 text-[var(--pm-magenta)]" />{t}</span>
          ))}
        </div>
      </section>

      <section id="features" className="max-w-7xl mx-auto px-5 lg:px-10 mt-24 scroll-mt-24">
        <p className="text-xs font-extrabold tracking-[0.16em] uppercase text-[#A0136E]">Features</p>
        <h2 className="text-3xl sm:text-4xl font-extrabold mt-2 max-w-2xl">Everything with a date, in one smart, secure place.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="pm-card p-7 hover:-translate-y-1 transition-transform duration-300 pm-fade-up" style={{ animationDelay: `${i * 0.06}s` }} data-testid={`feature-card-${i}`}>
              <div className="w-12 h-12 rounded-2xl grid place-items-center mb-5" style={{ background: "linear-gradient(135deg,#F3EEFF,#FFEFF5)" }}><f.icon className="w-5 h-5 text-[var(--pm-primary)]" /></div>
              <h3 className="font-bold text-lg">{f.title}</h3>
              <p className="text-[var(--pm-muted)] mt-2 text-sm leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="rounded-[28px] p-8 sm:p-14 text-white relative overflow-hidden" style={{ background: "var(--pm-gradient)" }} data-testid="how-it-works">
          <h2 className="text-3xl sm:text-4xl font-extrabold">{BRAND.slogan}</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {STEPS.map(([t, d], i) => (
              <div key={t} className="rounded-2xl bg-white/12 backdrop-blur-md border border-white/20 p-6">
                <span className="text-sm font-bold opacity-80">0{i + 1}</span>
                <h3 className="text-xl font-extrabold mt-2">{t}</h3>
                <p className="text-white/85 text-sm mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-10 mt-24" data-testid="faq-section">
        <h2 className="text-3xl font-extrabold mb-6">Questions, answered honestly</h2>
        <Accordion type="single" collapsible className="pm-card px-6">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`} className={i === FAQ.length - 1 ? "border-0" : ""}>
              <AccordionTrigger className="text-left font-semibold" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-[var(--pm-muted)] leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24 text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold">Start pinning your dates today.</h2>
        <p className="text-[var(--pm-muted)] mt-3">{BRAND.tagline}</p>
        <div className="flex flex-wrap justify-center gap-4 mt-6 text-sm text-[var(--pm-muted)]">
          {["Private by default", "Works on mobile", "Export anytime"].map((t) => <span key={t} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[var(--pm-success)]" />{t}</span>)}
        </div>
        <Link to={cta} className="pm-btn pm-btn-primary h-12 px-7 mt-8" data-testid="bottom-cta-btn">Get Started Free <ArrowRight className="w-4 h-4" /></Link>
      </section>
    </PublicLayout>
  );
}
